import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMultiPortfolio } from "@/context/MultiPortfolioContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Briefcase, ChevronDown, Plus, Check, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const PortfolioSwitcher = ({ collapsed }) => {
  const navigate = useNavigate();
  const { portfolios, currentPortfolio, switchPortfolio, createPortfolio, deletePortfolio, canCreateMore, loading } = useMultiPortfolio();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="px-3 py-2">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          {!collapsed && <span className="text-xs">Loading...</span>}
        </div>
      </div>
    );
  }

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error("Please enter a portfolio name");
      return;
    }
    setIsSubmitting(true);
    try {
      const newPortfolio = await createPortfolio(newName, newDescription);
      toast.success("Portfolio created!");
      setIsCreateOpen(false);
      setNewName("");
      setNewDescription("");
      // Switch to the new portfolio and redirect to dashboard
      if (newPortfolio?.portfolio_id) {
        await switchPortfolio(newPortfolio.portfolio_id);
      }
    } catch (error) {
      toast.error(error);
      setIsSubmitting(false);
    }
  };

  const handleSwitch = async (portfolioId) => {
    if (portfolioId === currentPortfolio?.portfolio_id) return;
    try {
      await switchPortfolio(portfolioId);
    } catch (error) {
      toast.error(error);
    }
  };

  const handleDelete = async (portfolioId, e) => {
    e.stopPropagation();
    if (!confirm("Are you sure? This will delete all assets, credits, and budget data in this portfolio.")) {
      return;
    }
    try {
      await deletePortfolio(portfolioId);
      toast.success("Portfolio deleted");
    } catch (error) {
      toast.error(error);
    }
  };

  if (collapsed) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="w-full h-10" data-testid="portfolio-switcher">
            <Briefcase className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
            Portfolios ({portfolios.length}/3)
          </div>
          <DropdownMenuSeparator />
          {portfolios.map((p) => (
            <DropdownMenuItem
              key={p.portfolio_id}
              onClick={() => handleSwitch(p.portfolio_id)}
              className="flex items-center justify-between cursor-pointer"
            >
              <span className="truncate">{p.name}</span>
              {p.portfolio_id === currentPortfolio?.portfolio_id && (
                <Check className="w-4 h-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}
          {canCreateMore && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setIsCreateOpen(true)} className="cursor-pointer">
                <Plus className="w-4 h-4 mr-2" />
                New Portfolio
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <>
      <div className="px-3 py-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-between text-sm h-9"
              data-testid="portfolio-switcher"
            >
              <div className="flex items-center gap-2 truncate">
                <Briefcase className="w-4 h-4 shrink-0" />
                <span className="truncate">{currentPortfolio?.name || "Select Portfolio"}</span>
              </div>
              <ChevronDown className="w-4 h-4 shrink-0 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground flex items-center justify-between">
              <span>Portfolios</span>
              <Badge variant="secondary" className="text-[10px]">{portfolios.length}/3</Badge>
            </div>
            <DropdownMenuSeparator />
            {portfolios.map((p) => (
              <DropdownMenuItem
                key={p.portfolio_id}
                onClick={() => handleSwitch(p.portfolio_id)}
                className="flex items-center justify-between cursor-pointer group"
              >
                <div className="flex items-center gap-2 truncate">
                  {p.portfolio_id === currentPortfolio?.portfolio_id ? (
                    <Check className="w-4 h-4 text-primary" />
                  ) : (
                    <div className="w-4 h-4" />
                  )}
                  <span className="truncate">{p.name}</span>
                  {p.is_default && (
                    <Badge variant="outline" className="text-[9px] px-1 py-0">Default</Badge>
                  )}
                </div>
                {!p.is_default && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100"
                    onClick={(e) => handleDelete(p.portfolio_id, e)}
                  >
                    <Trash2 className="w-3 h-3 text-destructive" />
                  </Button>
                )}
              </DropdownMenuItem>
            ))}
            {canCreateMore && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setIsCreateOpen(true)} className="cursor-pointer">
                  <Plus className="w-4 h-4 mr-2" />
                  New Portfolio
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Create Portfolio Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Portfolio</DialogTitle>
            <DialogDescription>
              Create a new portfolio to organize your assets separately. You can have up to 3 portfolios.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Portfolio Name</Label>
              <Input
                id="name"
                placeholder="e.g., Retirement, Kids Education"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                data-testid="portfolio-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Input
                id="description"
                placeholder="What is this portfolio for?"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={isSubmitting} data-testid="create-portfolio-btn">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Portfolio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PortfolioSwitcher;
